import { isNil } from "lodash";
import { X } from "lucide-react";
import React, { ReactNode } from "react";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  IconButton,
  SxProps,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Breakpoint } from "@mui/system";

import { IgButton } from "./IgButton";
import { IgTypography } from "./IgTypography";

const PREFIX = "IgDialog";

const classes = {
  dialogTitle: `${PREFIX}-dialogTitle`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialogTitle}`]: {
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "unset",
  },
}));

interface BaseDialogProps
  extends Omit<DialogProps, "open" | "title" | "content"> {
  showDialog: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  content?: ReactNode;
  rawContent?: ReactNode;
  yesButtonText?: ReactNode;
  noButtonText?: string | null;
  handleYesClick?: () => void;
  handleNoClick?: () => void;
  yesButtonDisabled?: boolean;
  handleClose: (event?: any, reason?: any) => void;
  loading?: boolean;
  shouldNotClose?: boolean;
  dialogTitleWidth?: string;
  padding?: string;
  dialogContentWidth?: string;
  hideActions?: boolean;
  minWidth?: string;
  maxWidth?: Breakpoint | false;
  dialogHeight?: string | null;
  showCloseIcon?: boolean;
  subtitleSx?: SxProps;
  contentSx?: SxProps;
  dialogImage?: ReactNode;
  dontUseDialogContent?: boolean;
  buttonVariant?: "text" | "outlined" | "contained";
  yesButtonColor?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  dialogContentPadding?: string;
  learnMoreButton?: ReactNode;
}

export const IgDialog = ({
  showDialog,
  handleClose,
  handleNoClick,
  title,
  yesButtonText,
  yesButtonDisabled,
  handleYesClick,
  noButtonText,
  loading,
  content,
  rawContent,
  maxWidth,
  minWidth,
  shouldNotClose,
  dialogTitleWidth,
  dialogContentWidth,
  dialogHeight,
  subtitle,
  showCloseIcon,
  hideActions,
  subtitleSx,
  padding,
  contentSx,
  dialogImage,
  dontUseDialogContent,
  buttonVariant,
  yesButtonColor,
  dialogContentPadding,
  learnMoreButton,
  ...rest
}: BaseDialogProps) => {
  const theme = useTheme();

  const onYesClickPress = () => {
    if (handleYesClick) {
      handleYesClick();
    }
    if (!shouldNotClose) {
      handleClose();
    }
  };

  const onNoClickPress = () => {
    if (handleNoClick) {
      handleNoClick();
    } else {
      handleClose();
    }
  };

  return (
    <StyledDialog
      open={showDialog}
      aria-labelledby={`${title as string}`}
      aria-describedby={`${title as string}`}
      onClose={handleClose}
      maxWidth={maxWidth || "xs"}
      scroll={"body"}
      PaperProps={{
        style: {
          borderRadius: "6px",
          width: maxWidth ? undefined : "460px",
          height: isNil(dialogHeight) ? undefined : dialogHeight || "275px",
          justifyContent: "center",
          minWidth: minWidth || "500px",
        },
      }}
      {...rest}
    >
      <Grid
        container
        direction={"column"}
        sx={{
          padding: padding || "24px 24px 24px 24px",
          minWidth: minWidth || undefined,
        }}
      >
        {title && (
          <DialogTitle
            sx={{
              width: dialogTitleWidth || "90%",
              padding: "0 0 4px 0",
            }}
            id="dialogBox-title"
            alignItems={"flex-start"}
            className={classes.dialogTitle}
          >
            <Grid
              container
              sx={{ flexWrap: "nowrap" }}
              alignItems={"flex-start"}
              justifyContent={"space-between"}
              columnSpacing={2}
            >
              <Grid container item direction={"column"} rowSpacing={1}>
                {dialogImage && (
                  <Grid item>
                    <>{dialogImage}</>
                  </Grid>
                )}
                <Grid container item justifyContent={"flex-start"}>
                  <IgTypography variant={"h3"} fontWeight={600}>
                    {title}
                  </IgTypography>
                </Grid>
              </Grid>
              {showCloseIcon && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "calc(100% - 56px)",
                    top: "12px",
                  }}
                >
                  <IconButton disabled={loading} onClick={() => handleClose()}>
                    <X size={24} />
                  </IconButton>
                </Box>
              )}
            </Grid>
          </DialogTitle>
        )}
        {subtitle && (
          <IgTypography
            color={theme.palette.text.secondary}
            sx={{ ...subtitleSx }}
          >
            <>{subtitle}</>
          </IgTypography>
        )}
        {content && (
          <DialogContent
            sx={{
              width: dialogContentWidth || "500px",
              padding: "24px 0 0 0",
              ...contentSx,
            }}
          >
            {content}
          </DialogContent>
        )}
        {rawContent && dontUseDialogContent ? (
          rawContent
        ) : rawContent ? (
          <DialogContent
            sx={{
              width: dialogContentWidth || "100%",
              padding: "24px 0 0 0 !important",
            }}
          >
            {rawContent}
          </DialogContent>
        ) : null}
        {hideActions ? null : (
          <DialogActions sx={{ padding: "24px 0 0 0" }}>
            <Grid
              item
              container
              direction={"row"}
              justifyContent={"space-between"}
              wrap={"nowrap"}
            >
              {learnMoreButton && <>{learnMoreButton}</>}
              <Grid
                container
                item
                justifyContent="end"
                direction={"row"}
                columnSpacing={1}
              >
                {noButtonText !== null && (
                  <Grid item sx={{ marginRight: "10px" }}>
                    <IgButton
                      size={"small"}
                      variant={buttonVariant ?? "contained"}
                      color={"secondary"}
                      disabled={loading}
                      onClick={() => {
                        onNoClickPress();
                      }}
                    >
                      {noButtonText || "Cancel"}
                    </IgButton>
                  </Grid>
                )}
                <Grid item>
                  <IgButton
                    size={"small"}
                    disabled={yesButtonDisabled}
                    loading={loading}
                    onClick={onYesClickPress}
                    color={yesButtonColor}
                  >
                    {yesButtonText || "Yes, discard my changes"}
                  </IgButton>
                </Grid>
              </Grid>
            </Grid>
          </DialogActions>
        )}
      </Grid>
    </StyledDialog>
  );
};
